import * as THREE from "three";
import { VISUAL } from "../config/visualConfig";
import type { Layer } from "../game/types";
import { squareFrameGeometry } from "./squareFrameGeometry";

/** Shared, bounded instance pools, including when the Go renders all 66 views. */
export class WarpEnvironment {
  readonly group = new THREE.Group();
  private readonly tunnel = new THREE.Group();
  private readonly streaks = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55, toneMapped: false, blending: THREE.AdditiveBlending, depthWrite: false }),
    160,
  );
  private readonly stars = Array.from({ length: 160 }, () => ({
    angle: Math.random() * Math.PI * 2, radius: 1 + Math.random() * 0.9,
    z: -24 + Math.random() * 21, speed: 3 + Math.random() * 4,
    length: 0.7 + Math.random() * 2,
  }));
  private readonly dust = Array.from({ length: 120 }, () => ({
    x: (Math.random() - 0.5) * 19, y: -10 + Math.random() * 21,
    z: -2.5 - Math.random() * 3, speed: 0.25 + Math.random() * 0.35,
    phase: Math.random() * Math.PI * 2,
  }));
  private readonly dustGeometry = new THREE.BufferGeometry();
  private readonly dustPositions = new THREE.Float32BufferAttribute(new Float32Array(120 * 3), 3);
  private readonly dustColors = new THREE.Float32BufferAttribute(new Float32Array(120 * 3), 3);
  private readonly matrix = new THREE.Matrix4();
  private readonly color = new THREE.Color();
  private elapsed = 0;
  private clearBurst: { age: number; duration: number; layer: Layer; lines: number } | null = null;
  private readonly burstRays = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1, 1, 1).translate(0, 0, -0.5),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.95, toneMapped: false, blending: THREE.AdditiveBlending, depthWrite: false }),
    240,
  );
  private readonly raySeeds = Array.from({ length: 240 }, () => ({
    angle: Math.random() * Math.PI * 2, radius: 0.85 + Math.random() * 1.1,
    delay: Math.random() * 0.35, length: 0.7 + Math.random() * 1.1,
    duration: 0.8 + Math.random() * 0.6,
  }));
  private readonly burstFrames = new THREE.InstancedMesh(
    squareFrameGeometry(0.975),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.9, toneMapped: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false }),
    6,
  );
  private readonly frameHalos = new THREE.InstancedMesh(
    squareFrameGeometry(0.94, 1.06),
    new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.14, toneMapped: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false }),
    6,
  );

  constructor() {
    this.streaks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.streaks.frustumCulled = false;
    this.tunnel.rotation.x = -VISUAL.cameraPitch;
    this.tunnel.add(this.streaks);
    for (const pool of [this.burstRays, this.burstFrames, this.frameHalos]) {
      pool.count = 0;
      pool.frustumCulled = false;
      pool.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.tunnel.add(pool);
    }
    this.dustPositions.setUsage(THREE.DynamicDrawUsage);
    this.dustColors.setUsage(THREE.DynamicDrawUsage);
    this.dustGeometry.setAttribute("position", this.dustPositions);
    this.dustGeometry.setAttribute("color", this.dustColors);
    const particles = new THREE.Points(this.dustGeometry, new THREE.PointsMaterial({ size: 0.04 * VISUAL.warpParticleScale, vertexColors: true, transparent: true, opacity: 0.55, toneMapped: false, blending: THREE.AdditiveBlending, depthWrite: false }));
    particles.frustumCulled = false;

    // Sparse rings put a vanishing point behind the board rather than a flat wall.
    const points: number[] = [];
    for (const z of [-6, -12, -20]) {
      for (let i = 0; i < 48; i++) {
        for (const angle of [i / 48 * Math.PI * 2, (i + 1) / 48 * Math.PI * 2]) {
          points.push(Math.cos(angle) * 6.5, Math.sin(angle) * 11, z);
        }
      }
    }
    const ringGeometry = new THREE.BufferGeometry();
    ringGeometry.setAttribute("position", new THREE.Float32BufferAttribute(points, 3));
    this.tunnel.add(new THREE.LineSegments(ringGeometry, new THREE.LineBasicMaterial({ color: 0x276379, transparent: true, opacity: 0.1, blending: THREE.AdditiveBlending, depthWrite: false })));
    this.group.add(this.tunnel, particles);
    this.update(0);
  }

  burst(layer: Layer, lines: number): void {
    this.clearBurst = { age: 0, duration: 1.8 + Math.min(4, lines) * 0.14, layer, lines: Math.min(4, lines) };
  }

  resetBurst(): void {
    this.clearBurst = null;
    this.burstRays.count = this.burstFrames.count = this.frameHalos.count = 0;
  }

  get isBursting(): boolean { return this.clearBurst !== null; }

  update(delta: number, acceleration = 1): void {
    this.elapsed += delta;
    if (this.clearBurst) {
      this.clearBurst.age += delta;
      if (this.clearBurst.age >= this.clearBurst.duration) this.resetBurst();
    }
    const boost = this.clearBurst ? (1 - this.clearBurst.age / this.clearBurst.duration) ** 1.5 : 0;
    const strength = this.clearBurst?.lines ?? 1;
    const speed = acceleration + boost * (7 + strength * 2);
    (this.streaks.material as THREE.MeshBasicMaterial).opacity = 0.55 + boost * 0.4;
    this.stars.forEach((star, i) => {
      star.z += delta * star.speed * speed;
      if (star.z > -3) star.z = -24 + (star.z + 3) % 21;
      const fade = Math.min(1, (-3 - star.z) / 2, (star.z + 24) / 2);
      const thickness = (0.025 + boost * 0.025) * VISUAL.warpParticleScale;
      this.matrix.makeScale(thickness, thickness, Math.min(8, star.length * acceleration * (1 + boost * 3)));
      this.matrix.setPosition(Math.cos(star.angle) * 4.5 * star.radius, Math.sin(star.angle) * 8 * star.radius, star.z);
      this.streaks.setMatrixAt(i, this.matrix);
      const hue = this.clearBurst ? (this.clearBurst.layer === "front" ? VISUAL.front : VISUAL.back) : (i % 5 === 0 ? VISUAL.back : VISUAL.front);
      this.color.setHex(boost > 0.2 && i % 5 === 0 ? 0xdffcff : hue).multiplyScalar(Math.max(0, fade) * (0.8 + boost * 1.1));
      this.streaks.setColorAt(i, this.color);
    });
    this.streaks.instanceMatrix.needsUpdate = true;
    if (this.streaks.instanceColor) this.streaks.instanceColor.needsUpdate = true;
    this.dust.forEach((particle, i) => {
      particle.y += delta * particle.speed;
      if (particle.y > 11) particle.y -= 21;
      const fade = Math.min(1, (particle.y + 10) / 2, (11 - particle.y) / 2);
      this.dustPositions.setXYZ(i, particle.x + Math.sin(this.elapsed * 0.4 + particle.phase) * 0.15, particle.y, particle.z);
      this.color.setHex(i % 7 === 0 ? VISUAL.back : VISUAL.front).multiplyScalar(Math.max(0, fade) * 0.55);
      this.dustColors.setXYZ(i, this.color.r, this.color.g, this.color.b);
    });
    this.dustPositions.needsUpdate = true;
    this.dustColors.needsUpdate = true;
    this.updateBurst();
  }

  private updateBurst(): void {
    const burst = this.clearBurst;
    if (!burst) return;
    const hue = burst.layer === "front" ? VISUAL.front : VISUAL.back;
    let rays = 0;
    for (let i = 0; i < 120 + burst.lines * 30; i++) {
      const seed = this.raySeeds[i]!;
      const progress = (burst.age - seed.delay) / seed.duration;
      if (progress < 0 || progress >= 1) continue;
      const fade = Math.sin(progress * Math.PI) ** 0.45;
      // Light starts deep in the tunnel and passes through the focal plane.
      // Keep the final foreground depth shallow and the rays at the perimeter.
      const z = THREE.MathUtils.lerp(-28, 1.5, progress);
      const radius = seed.radius * (0.85 + progress * 0.25);
      this.matrix.makeScale(0.035 + burst.lines * 0.007, 0.035 + burst.lines * 0.007, seed.length * (2 + progress * 3));
      const x = Math.cos(seed.angle), y = Math.sin(seed.angle);
      const edge = Math.max(Math.abs(x), Math.abs(y));
      this.matrix.setPosition(x / edge * 4.7 * radius, y / edge * 8.2 * radius, z);
      this.burstRays.setMatrixAt(rays, this.matrix);
      this.color.setHex(i % 4 === 0 ? 0xffffff : hue).multiplyScalar(fade * 1.8);
      this.burstRays.setColorAt(rays++, this.color);
    }
    this.burstRays.count = rays;
    let frames = 0;
    for (let i = 0; i < 2 + burst.lines; i++) {
      const progress = (burst.age - i * 0.14) / 1.15;
      if (progress < 0 || progress >= 1) continue;
      const fade = Math.sin(progress * Math.PI) ** 0.55;
      const size = 0.85 + progress * 0.35;
      this.matrix.makeScale(4.5 * size, 7.8 * size, 1);
      // The staggered square frames travel in 3D from behind the board to
      // just in front of it; perspective makes them approach the viewer.
      this.matrix.setPosition(0, 0, THREE.MathUtils.lerp(-26, 1.5, progress));
      this.burstFrames.setMatrixAt(frames, this.matrix);
      this.frameHalos.setMatrixAt(frames, this.matrix);
      this.color.setHex(i % 3 === 0 ? 0xdffcff : hue).multiplyScalar(fade * 1.5);
      this.burstFrames.setColorAt(frames, this.color);
      this.frameHalos.setColorAt(frames++, this.color);
    }
    this.burstFrames.count = this.frameHalos.count = frames;
    for (const pool of [this.burstRays, this.burstFrames, this.frameHalos]) {
      pool.instanceMatrix.needsUpdate = true;
      if (pool.instanceColor) pool.instanceColor.needsUpdate = true;
    }
  }
}
